import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { useViewCurrency } from "@/context/ViewCurrencyContext";
import { House, Buildings, Users, Wallet, Receipt, CalendarBlank, ChartBar, SignOut, Globe, CurrencyCircleDollar } from "@phosphor-icons/react";

function NavItem({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testid}
      className={({ isActive }) =>
        `nav-link flex items-center gap-3 px-4 py-2.5 rounded-md text-sm ${
          isActive
            ? "bg-slate-900 text-white"
            : "text-slate-700 hover:bg-slate-100"
        }`
      }
    >
      <Icon size={18} weight="duotone" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { viewCurrency, setViewCurrency } = useViewCurrency();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <div className="label-eyebrow">Real Estate</div>
          <div className="font-heading text-2xl font-semibold tracking-tight text-slate-900 mt-1">
            KiraTakip
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavItem to="/" icon={House} label={t("dashboard")} testid="nav-dashboard" />
          <NavItem to="/properties" icon={Buildings} label={t("properties")} testid="nav-properties" />
          <NavItem to="/tenants" icon={Users} label={t("tenants")} testid="nav-tenants" />
          <NavItem to="/payments" icon={Wallet} label={t("payments")} testid="nav-payments" />
          <NavItem to="/expenses" icon={Receipt} label={t("expenses")} testid="nav-expenses" />
          <NavItem to="/calendar" icon={CalendarBlank} label={t("calendar")} testid="nav-calendar" />
          <NavItem to="/reports" icon={ChartBar} label={t("reports")} testid="nav-reports" />
        </nav>
        <div className="p-4 border-t border-slate-200 space-y-3">
          <div>
            <div className="label-eyebrow mb-1.5 flex items-center gap-1.5">
              <CurrencyCircleDollar size={12} weight="duotone" /> {t("view_currency")}
            </div>
            <div className="grid grid-cols-3 gap-1" data-testid="view-currency-toggle">
              {["TRY", "USD", "EUR"].map((c) => (
                <button key={c} data-testid={`view-cur-${c}`}
                  onClick={() => setViewCurrency(c)}
                  className={`text-xs uppercase font-bold py-1.5 rounded ${viewCurrency === c ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "tr" ? "en" : "tr")}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-semibold text-slate-600 hover:bg-slate-100"
          >
            <span className="flex items-center gap-2"><Globe size={16} weight="duotone" /> LANG</span>
            <span className="uppercase tracking-widest">{lang}</span>
          </button>
          <div className="text-xs text-slate-500 truncate" data-testid="user-email">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100"
          >
            <SignOut size={16} /> {t("logout")}
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="max-w-[1400px] mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
